/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, { attr } from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import type { Type } from '@warp-drive/core/types/symbols';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import ConnectionStatus from '@linc-technologies/linced-common/enums/connection-status';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import WondeDomain from '@linc-technologies/linced-common/enums/wonde-domain';
import WondeSISType from '@linc-technologies/linced-common/enums/wonde-sis-type';
import moment from 'moment';

// Proto: provider_wonde.Connection.
export default class Connection extends Model {
  declare [Type]: 'wonde/connection';

  // createTime is when the resource was created in seconds from the epoch.
  @attr('number')
  declare createTime: number | undefined;

  // updateTime is the last time the resource was modified in seconds from the epoch.
  @attr('number')
  declare updateTime: number | undefined;

  // wondeId is the unique wonde school id.
  @attr('string', { defaultValue: '' })
  declare wondeId: string;

  // schoolId is the school's unique Hero identifier.
  @attr('string', { defaultValue: '' })
  declare schoolId: string;

  // jurisdiction specifies what educational jurisdiction the school is a part of.
  @attr('number', { defaultValue: 0 })
  declare jurisdiction: EnumValue<typeof Jurisdiction> | 0;

  @computed('jurisdiction')
  get displayJurisdiction() {
    return Jurisdiction.getLabel(this.jurisdiction);
  }

  // SIS is the school's student information system.
  @attr('number', { defaultValue: 0 })
  declare sis: EnumValue<typeof WondeSISType> | 0;

  @computed('sis')
  get displaySis() {
    return WondeSISType.getLabel(this.sis);
  }

  // Domain tells us which client to use (World/Aunz)
  @attr('number', { defaultValue: 0 })
  declare domain: EnumValue<typeof WondeDomain> | 0;

  @computed('domain')
  get displayDomain() {
    return WondeDomain.getLabel(this.domain);
  }

  // syncEnabled determines whether school can read data from Wonde and
  // update the hero school's resources.
  @attr('boolean', { defaultValue: false })
  declare syncEnabled: boolean;

  // periodicSyncEnabled determines whether to run a weekly sync with Wonde.
  @attr('boolean', { defaultValue: false })
  declare periodicSyncEnabled: boolean;

  // lastSyncTime stores the last time we synced from Wonde. This allows us to only pull
  // data from this point onwards.
  @attr('number', { defaultValue: 0 })
  declare lastSyncTime: number;

  @computed('lastSyncTime')
  get displaySyncTime() {
    if (!this.lastSyncTime) {
      return 'Never';
    }
    return moment.unix(this.lastSyncTime).format('YYYY/MM/DD h:mmA');
  }

  // status contains the current level of approval.
  @attr('number', { defaultValue: 0 })
  declare status: EnumValue<typeof ConnectionStatus> | 0;

  @computed('status')
  get displayStatus() {
    return ConnectionStatus.getLabel(this.status);
  }
}
